import React, { useEffect, useMemo, useState } from 'react';
import {
    ClaimTransmissionStatus,
    DocumentViewer,
    ProviderClaimTransmission,
    SupportClaimTransmission,
    TooltipTransitionComponent,
    TransmissionStatusBadge,
    renderDate,
    Spinner,
} from '../../../..';
import { PayloadComparisonViewer } from '../../../payload-comparison-viewer';
import { mdiRomanNumeral1, mdiRomanNumeral2, mdiRomanNumeral3 } from '@mdi/js';
import { Tooltip } from '@mui/material';
import _ from 'lodash';
import ContentLoader from 'react-content-loader';
import { ITransmissionDetailProps } from './transmission-detail.types';
import * as S from '../claim-timeline.styles';

const LinePlaceholder = React.memo(() => (
    <ContentLoader speed={2} height={20} style={{ width: '100%' }} preserveAspectRatio="">
        <rect x="0" y="0" rx="0" ry="0" width="50%" height="20" />
    </ContentLoader>
));

export interface ITransmissionWithPaymentIds {
    paymentIds?: string[];
}

export const TransmissionStatus: React.FunctionComponent<{
    transmission: ITransmissionWithPaymentIds;
}> = ({ transmission }) => {
    if (!transmission.paymentIds || transmission.paymentIds.length === 0) {
        return (
            <Tooltip
                placement="top"
                title="Primary"
                TransitionComponent={TooltipTransitionComponent}
                enterTouchDelay={0}>
                <S.FlexWrapper>
                    <S.SecondaryIcon path={mdiRomanNumeral1} size={1} />
                </S.FlexWrapper>
            </Tooltip>
        );
    }
    if (transmission.paymentIds.length === 1) {
        return (
            <Tooltip
                placement="top"
                title="Secondary"
                TransitionComponent={TooltipTransitionComponent}
                enterTouchDelay={0}>
                <S.FlexWrapper>
                    <S.SecondaryIcon path={mdiRomanNumeral2} size={1} />
                </S.FlexWrapper>
            </Tooltip>
        );
    }
    if (transmission.paymentIds.length === 2) {
        return (
            <Tooltip
                placement="top"
                title="Tertiary"
                TransitionComponent={TooltipTransitionComponent}
                enterTouchDelay={0}>
                <S.FlexWrapper>
                    <S.SecondaryIcon path={mdiRomanNumeral3} size={1} />
                </S.FlexWrapper>
            </Tooltip>
        );
    }
    return null;
};

const SupportStatusBlock: React.FunctionComponent<{ status: ClaimTransmissionStatus }> = ({ status }) => (
    <>
        <S.ExpandSubtitle>{status.status && <TransmissionStatusBadge status={status.status} size="small" />}</S.ExpandSubtitle>
        <S.ExpandTable expand={true}>
            <S.ExpandTBody>
                <S.ExpandTR>
                    <S.ExpandTH>External ID</S.ExpandTH>
                    <S.ExpandTD>{status.externalId}</S.ExpandTD>
                </S.ExpandTR>
                <S.ExpandTR>
                    <S.ExpandTH>Message</S.ExpandTH>
                    <S.ExpandTD>{status.messages}</S.ExpandTD>
                </S.ExpandTR>
                <S.ExpandTR>
                    <S.ExpandTH>Message Code</S.ExpandTH>
                    <S.ExpandTD>{status.messageCode}</S.ExpandTD>
                </S.ExpandTR>
                <S.ExpandTR>
                    <S.ExpandTH>Fields</S.ExpandTH>
                    <S.ExpandTD>{status.fields}</S.ExpandTD>
                </S.ExpandTR>
                <S.ExpandTR>
                    <S.ExpandTH>Created</S.ExpandTH>
                    <S.ExpandTD>{status.created ? renderDate(status.created) : ''}</S.ExpandTD>
                </S.ExpandTR>
            </S.ExpandTBody>
        </S.ExpandTable>
    </>
);

export const TransmissionDetail: React.FunctionComponent<ITransmissionDetailProps> = (props) => {
    const {
        transmission,
        appType,
        previousTransmission,
        downloadDocument,
        getClearinghousePayer,
        getPayerSubscriber,
    } = props;

    const showDiff = !!previousTransmission?.x12 && !!transmission.x12;

    const [clearinghousePayer, setClearinghousePayer] = useState<any>();
    const [payer, setPayer] = useState<any>();
    const [subscriber, setSubscriber] = useState<any>();
    const [documentUrl, setDocumentUrl] = useState<{ url?: string } | undefined>();
    const [loadingPayer, setLoadingPayer] = useState(false);
    const [documentLoading, setDocumentLoading] = useState(false);

    const documentId = transmission.documentId;

    useEffect(() => {
        if (appType !== 'support' || !getClearinghousePayer) return;
        const id = (transmission as SupportClaimTransmission).clearinghousePayerId;
        if (!id) return;
        setLoadingPayer(true);
        getClearinghousePayer(id)
            .then((res) => {
                if (res) setClearinghousePayer(res);
            })
            .finally(() => setLoadingPayer(false));
    }, [appType, transmission, getClearinghousePayer]);

    useEffect(() => {
        if (appType !== 'provider' || !getPayerSubscriber) return;
        const id = (transmission as ProviderClaimTransmission).payerSubscriberId;
        if (!id) return;
        setLoadingPayer(true);
        getPayerSubscriber(id)
            .then((res) => {
                if (res) {
                    setPayer(res.payer);
                    setSubscriber(res.subscriber);
                }
            })
            .finally(() => setLoadingPayer(false));
    }, [appType, transmission, getPayerSubscriber]);

    useEffect(() => {
        if (!documentId) return;
        setDocumentLoading(true);
        downloadDocument(documentId)
            .then(setDocumentUrl)
            .finally(() => setDocumentLoading(false));
    }, [documentId, downloadDocument]);

    const sortedStatuses = useMemo(
        () =>
            (transmission.statuses || []).slice().sort(
                (a, b) => _.toNumber(b.externalId) - _.toNumber(a.externalId)
            ),
        [transmission.statuses]
    );

    return (
        <S.ExpandWrap key={transmission.id}>
            <S.ExpandContainer>
                <S.ExpandTable expand={true}>
                    <S.ExpandTBody>
                        {loadingPayer && (
                            <S.ExpandTR>
                                <S.ExpandTD colSpan={3}>
                                    <Spinner />
                                </S.ExpandTD>
                            </S.ExpandTR>
                        )}
                        {!loadingPayer &&
                            (appType === 'support' ? (
                                <>
                                    <S.ExpandTR>
                                        <S.ExpandTH>ID</S.ExpandTH>
                                        <S.ExpandTD>{transmission.id}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>External ID</S.ExpandTH>
                                        <S.ExpandTD>{transmission.externalId}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Type</S.ExpandTH>
                                        <S.ExpandTD>{transmission.type}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Clearinghouse</S.ExpandTH>
                                        <S.ExpandTD>{clearinghousePayer?.clearinghouse?.name}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Payer</S.ExpandTH>
                                        <S.ExpandTD>{clearinghousePayer?.payer?.name}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Sender</S.ExpandTH>
                                        <S.ExpandTD>{(transmission as SupportClaimTransmission).senderName}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Response Time</S.ExpandTH>
                                        <S.ExpandTD>{(transmission as SupportClaimTransmission).responseTime}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Payer ICN</S.ExpandTH>
                                        <S.ExpandTD>{transmission.payerIcn}</S.ExpandTD>
                                    </S.ExpandTR>
                                </>
                            ) : (
                                <>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Payer</S.ExpandTH>
                                        <S.ExpandTD colSpan={3}>
                                            {_.get(transmission, 'clearinghousePayer.payer.name') || payer?.name}
                                        </S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Subscriber</S.ExpandTH>
                                        <S.ExpandTD colSpan={3}>
                                            {subscriber &&
                                                _.compact([
                                                    subscriber.first,
                                                    subscriber.middle,
                                                    subscriber.last,
                                                ]).join(' ')}
                                        </S.ExpandTD>
                                    </S.ExpandTR>
                                    {transmission.type && (
                                        <S.ExpandTR>
                                            <S.ExpandTH>Type</S.ExpandTH>
                                            <S.ExpandTD colSpan={3}>{transmission.type}</S.ExpandTD>
                                        </S.ExpandTR>
                                    )}
                                    {transmission.externalId && (
                                        <S.ExpandTR>
                                            <S.ExpandTH>External ID</S.ExpandTH>
                                            <S.ExpandTD colSpan={3}>{transmission.externalId}</S.ExpandTD>
                                        </S.ExpandTR>
                                    )}
                                    {transmission.payerIcn && (
                                        <S.ExpandTR>
                                            <S.ExpandTH>Payer ICN</S.ExpandTH>
                                            <S.ExpandTD colSpan={3}>{transmission.payerIcn}</S.ExpandTD>
                                        </S.ExpandTR>
                                    )}
                                </>
                            ))}
                    </S.ExpandTBody>
                    {appType === 'provider' && sortedStatuses.length > 0 && (
                        <S.ExpandTBody>
                            <S.ExpandTR>
                                <S.ExpandTD>
                                    <S.ExpandTitle>Payer Responses</S.ExpandTitle>
                                </S.ExpandTD>
                            </S.ExpandTR>
                            <S.ExpandTR>
                                <S.ExpandSubTH>Status</S.ExpandSubTH>
                                <S.ExpandSubTH>Code</S.ExpandSubTH>
                                <S.ExpandSubTH>Message</S.ExpandSubTH>
                            </S.ExpandTR>
                            {sortedStatuses.map((status, i) => (
                                <S.ExpandTR key={i}>
                                    <S.ExpandTD>{status.status}</S.ExpandTD>
                                    <S.ExpandTD>{status.messageCode}</S.ExpandTD>
                                    <S.ExpandTD>{status.messages}</S.ExpandTD>
                                </S.ExpandTR>
                            ))}
                        </S.ExpandTBody>
                    )}
                </S.ExpandTable>
                {documentLoading && <LinePlaceholder />}
                {documentUrl?.url && <DocumentViewer url={documentUrl.url} />}
                {appType === 'support' && sortedStatuses.length > 0 && (
                    <S.ExpandListContainer>
                        {sortedStatuses.map((status, index) => (
                            <S.ExpandListItem key={`${status.externalId}-${index}`}>
                                <SupportStatusBlock status={status} />
                            </S.ExpandListItem>
                        ))}
                    </S.ExpandListContainer>
                )}
                <PayloadComparisonViewer
                    currentX12={transmission.x12 || ''}
                    previousX12={previousTransmission?.x12}
                    previousTransmissionId={previousTransmission?.id}
                    showDiff={showDiff}
                />
            </S.ExpandContainer>
        </S.ExpandWrap>
    );
};
